import React, { useCallback, useEffect, useMemo } from 'react';
import { useField } from 'react-final-form';
import { observer } from 'mobx-react';
import { DateField } from 'ksnm-common-ui/lib/Form/DateField';
import { TextField } from 'ksnm-common-ui/lib/Form/InputFields';
import { SelectField } from 'ksnm-common-ui/lib/Form/SelectField';
import { TextAreaField } from 'ksnm-common-ui/lib/Form/TextAreaField';
import { maxBy, sortBy } from 'lodash-es';

import { controlsStore } from '@stores/dictionaryStores/controlsStore/controlsStore';
import { controlVersionEditStore } from '@stores/dictionaryStores/controlVersionsStore/controlVersionEditStore';
import { controlVersionsStore } from '@stores/dictionaryStores/controlVersionsStore/controlVersionsStore';
import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';
import { reportGroupsStore } from '@stores/dictionaryStores/reportGroupsStore/reportGroupsStore';
import type { ReportGroupsDto } from '@src/api';

import styles from './Form.module.scss';

const renderName = (value: ReportGroupsDto) => `${value.code} - ${value.name}`;

export const Form = observer(() => {
  const { isEditMode, isNew } = controlVersionEditStore;
  const { controlVersions } = controlVersionsStore;
  const { controls } = controlsStore;
  const { reportGroups } = reportGroupsStore;

  useEffect(() => {
    fetchReferences(['controls', 'controlVersions', 'stepTypes', 'reportGroups']);
  }, []);

  const {
    input: { value: repGroupCode },
  } = useField('repGroupCode');

  const {
    input: { value: ctrlCode, onChange: changeCtrlCode },
  } = useField('ctrlCode');

  const {
    input: { onChange: changeCtrlVersCode },
  } = useField('ctrlVersCode');

  const {
    input: { onChange: changeVersionNum },
  } = useField('versionNum');

  const {
    input: { onChange: changeCtrlVersExt },
  } = useField('ctrlVersExt');

  useEffect(() => {
    if (isNew && ctrlCode) {
      const data = controlVersions?.filter((version) => version.ctrlCode === ctrlCode) ?? [];
      const ctrlExt = controls.find((control) => control.ctrlCode === ctrlCode)?.ctrlExt;

      if (data) {
        const maxVersion = (maxBy(data, 'versionNum')?.versionNum ?? 0) + 1;
        const formatMaxVersion = String(maxVersion).padStart(3, '0');
        changeCtrlVersCode(`${ctrlCode}_${formatMaxVersion}`);
        changeVersionNum(formatMaxVersion);
        changeCtrlVersExt(`${ctrlExt}_${formatMaxVersion}`);
      }
    }
  }, [changeCtrlVersCode, changeCtrlVersExt, changeVersionNum, controlVersions, controls, ctrlCode, isNew]);

  const ctrlCodeItems = useMemo(
    () =>
      sortBy(
        controls.filter((item) => item.repGroupCode === repGroupCode),
        'ctrlCode',
      ).map((control) => ({
        name: `${control.ctrlCode} - ${control.ctrlExt} - ${control.ctrlName}`,
        id: control.ctrlCode,
      })),
    [controls, repGroupCode],
  );

  const onChange = useCallback(() => {
    changeCtrlCode(undefined);
    changeCtrlVersCode(undefined);
    changeVersionNum(undefined);
    changeCtrlVersExt(undefined);
  }, [changeCtrlCode, changeCtrlVersCode, changeCtrlVersExt, changeVersionNum]);

  return (
    <div>
      <TextField
        name="ctrlVersCode"
        label="Код версии контроля"
        disabled
        size="small"
        groupStyle={!isEditMode}
        required
        maxLength={15}
      />
      <SelectField
        items={reportGroups}
        name="repGroupCode"
        label="Код группы отчетных форм"
        disabled={!isNew}
        size="small"
        groupStyle={!isEditMode}
        renderName={renderName}
        required
        onChange={onChange}
      />
      <SelectField
        items={ctrlCodeItems}
        name="ctrlCode"
        label="Код контроля"
        disabled={!isNew || !repGroupCode}
        size="small"
        groupStyle={!isEditMode}
        allowClear
        required
      />
      <div className={styles.row}>
        <TextField name="versionNum" label="Номер версии" disabled size="small" groupStyle={!isEditMode} required />
        <DateField
          label="Дата с"
          name="dateFrom"
          required
          disabled={!isEditMode}
          size="small"
          groupStyle={!isEditMode}
        />
        <DateField label="Дата до" name="dateTo" disabled={!isEditMode} size="small" groupStyle={!isEditMode} />
        <TextField
          name="ctrlVersExt"
          label="Внешний код версии контроля"
          disabled
          size="small"
          groupStyle={!isEditMode}
          required
          maxLength={100}
        />
      </div>
      <div className={styles.row}>
        <TextAreaField
          name="ctrlVersDesc"
          label="Описание контроля"
          disabled={!isEditMode}
          size="small"
          groupStyle={!isEditMode}
          required
          maxLength={1000}
          autoSize
        />

        <TextAreaField
          name="textSuccess"
          label="Описание статуса (успех)"
          disabled={!isEditMode}
          size="small"
          groupStyle={!isEditMode}
          required
          maxLength={1000}
          autoSize
        />
        <TextAreaField
          name="textFail"
          label="Описание статуса (расхождение)"
          disabled={!isEditMode}
          size="small"
          groupStyle={!isEditMode}
          required
          maxLength={1000}
          autoSize
        />
      </div>
    </div>
  );
});
